import React, { useState } from 'react'
import { PrimaryButton, SecondaryButton } from '../../../components/Button'
import Modal from '../../../components/Modal'
import { SectionBreakerHorizontal, SubTitle } from '../../../components/Shared'
import { HEDGED_PAYOUT_FUNDING_NETWORK, PayoutType, getHedgedPayoutDestinationStatus } from './payoutTypes'
import { RADOM_COLORS } from '../../../util/Constants'
import Checkbox from '../../../components/Checkbox'
import { ManagedPaymentMethodSelector } from '../../../components/ManagedMethodMultiselector'
import { InputLabel } from '../../../components/Input'
import SearchMultiselect from '../../../components/SearchMultiselect'
import { AddButton } from '../../../components/CreateDepositAddressModal'
import CustomerForm from '../../../components/CustomerForm'
import { ManagedPaymentMethod, getPaymentMethods } from '../../../util/Managed'
import { Customer, ExternalAccount, PayoutCapabilitiesResponse, PayoutFundingCurrency } from '../../../state/Radom'
import { Currencies, Currency } from '../../../util/Currencies'
import ExternalAccountSelectorTag from './ExternalAccountSelectorTag'
import { isCryptoExternalAccount, isEurIbanExternalAccount } from '../../../util/ExternalAccounts'
import CreateExternalAccountModal, { PayoutAccountCategory } from '../../Customers/CreateExternalAccountModal'
import { AssetRow } from '../../../components/AssetIdentity'
import { assetLineFor, assetName, methodNetworkId } from '../../../util/AssetLabels'

// The registry entry for a stablecoin balance on the hedged funding network, so the option shows its logo and name.
const hedgedFundingMethod = (currency: PayoutFundingCurrency): ManagedPaymentMethod | undefined =>
  getPaymentMethods().find(m =>
    m.ticker === currency && methodNetworkId(m) === HEDGED_PAYOUT_FUNDING_NETWORK && !m.isTestnet && !m.isHidden)

/** One stablecoin balance to fund a hedged payout from, named the way the asset pickers name assets. */
const HedgedFundingOption = ({ currency }: { currency: PayoutFundingCurrency }): JSX.Element => {
  const method = hedgedFundingMethod(currency)
  if (method) return <AssetRow method={method} size={22} />
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
      <span style={{ fontWeight: 500 }}>{currency}</span>
      <span style={{ color: RADOM_COLORS.GRAY_DARKER, fontSize: 11.5 }}>
        {assetLineFor(currency, HEDGED_PAYOUT_FUNDING_NETWORK)}
      </span>
    </div>
  )
}

interface ISelectAssetsProps {
  payoutType: PayoutType
  setPayoutType: (type: PayoutType) => void
  testMode: boolean
  selectedFundingMethod: ManagedPaymentMethod | undefined
  setSelectedFundingMethod: (method: ManagedPaymentMethod | undefined) => void
  setSelectedPayoutMethod: (method: ManagedPaymentMethod | Currency | undefined) => void
  selectedPayoutMethod: ManagedPaymentMethod | Currency | undefined
  stablecoinFundingEnabled: boolean
  fundWithStablecoin: boolean
  onToggleStablecoinFunding: (enabled: boolean) => void
  fundingCurrency: PayoutFundingCurrency
  setFundingCurrency: (currency: PayoutFundingCurrency) => void
  isLoadingCustomer: boolean
  customers: Customer[]
  selectedCustomer: Customer | null
  setSelectedCustomer: (customer: Customer | null) => void
  customerFormVisible: boolean
  setCustomerFormVisible: (visible: boolean) => void
  loadCustomers: () => Promise<void>
  onContinue: () => void
  payoutCapabilities: PayoutCapabilitiesResponse | null
  externalAccounts: ExternalAccount[]
  selectedExternalAccount: ExternalAccount | undefined
  setSelectedExternalAccount: (account: ExternalAccount | undefined) => void
  isLoadingExternalAccounts: boolean
  onExternalAccountCreated: () => Promise<void>
}

const SelectAssets = ({
  payoutType,
  setPayoutType,
  testMode,
  selectedFundingMethod,
  setSelectedFundingMethod,
  selectedPayoutMethod,
  setSelectedPayoutMethod,
  stablecoinFundingEnabled,
  fundWithStablecoin,
  onToggleStablecoinFunding,
  fundingCurrency,
  setFundingCurrency,
  isLoadingCustomer,
  customers,
  selectedCustomer,
  setSelectedCustomer,
  customerFormVisible,
  setCustomerFormVisible,
  loadCustomers,
  onContinue,
  payoutCapabilities,
  externalAccounts,
  selectedExternalAccount,
  setSelectedExternalAccount,
  isLoadingExternalAccounts,
  onExternalAccountCreated
}: ISelectAssetsProps): JSX.Element => {
  const [createAccountModalOpen, setCreateAccountModalOpen] = useState(false)
  const isFiat = payoutType === PayoutType.Fiat
  const isHedged = payoutType === PayoutType.Crypto && fundWithStablecoin && !testMode

  const hedgedDestinationMethod = isHedged && selectedPayoutMethod && !('icon' in selectedPayoutMethod)
    ? selectedPayoutMethod
    : undefined
  const hedgedDestinationNetwork = hedgedDestinationMethod
    ? hedgedDestinationMethod.hostChain?.name ?? hedgedDestinationMethod.name
    : undefined

  const accountCreationMethod: ManagedPaymentMethod | undefined = (() => {
    if (isFiat) return undefined
    if (isHedged) {
      if (!hedgedDestinationNetwork) return undefined
      return getPaymentMethods().find(m =>
        !m.hostChain && m.name === hedgedDestinationNetwork && !m.isTestnet)
    }
    if (selectedFundingMethod) {
      const networkName = selectedFundingMethod.hostChain?.name ?? selectedFundingMethod.name
      return getPaymentMethods().find(m => !m.hostChain && m.name === networkName && !!m.isTestnet === testMode)
    }
    return undefined
  })()

  const filteredExternalAccounts = isFiat
    ? externalAccounts.filter(isEurIbanExternalAccount)
    : externalAccounts.filter(a => a.active && isCryptoExternalAccount(a) &&
        (!isHedged || !hedgedDestinationNetwork || a.crypto?.network === hedgedDestinationNetwork))

  const isTokenMatch = (a?: string, b?: string): boolean => {
    if (!a && !b) return true
    if (!a || !b) return false
    if (a.startsWith('0x') || b.startsWith('0x')) {
      return a.toLowerCase() === b.toLowerCase()
    }
    return a === b
  }

  const getLiveCapabilityStatus = (method: ManagedPaymentMethod): { available: boolean, reason?: string } => {
    const network = method.hostChain?.name ?? method.name
    const token = method.hostChain?.tokenAddress
    const hasMatch = payoutCapabilities?.fundedDestinations.some(cap => (
      cap.fundingCurrency === fundingCurrency &&
      cap.network === network &&
      isTokenMatch(cap.token, token)
    ))

    return hasMatch
      ? { available: true }
      : { available: false, reason: 'Unavailable on provider right now' }
  }

  const getDestinationStatus = (method: ManagedPaymentMethod): { available: boolean, reason?: string } => {
    if (payoutCapabilities) {
      return getLiveCapabilityStatus(method)
    }
    return getHedgedPayoutDestinationStatus(method, fundingCurrency)
  }

  const isStablecoinDestinationMethod = (method: ManagedPaymentMethod): boolean =>
    !!method.hostChain &&
    ['USDC', 'USDT'].includes(method.ticker.toUpperCase()) &&
    !method.isHidden &&
    !method.isFiat &&
    !method.isTestnet

  // Fiat payouts are funded from the Ethereum stablecoin balances the backend
  // accepts as fundingCurrency values (USDC | USDT).
  const fiatFundingNetwork = testMode ? 'SepoliaTestnet' : 'Ethereum'
  const isFiatFundingMethod = (method: ManagedPaymentMethod): boolean =>
    !!method.hostChain &&
    method.hostChain.name === fiatFundingNetwork &&
    ['USDC', 'USDT'].includes(method.ticker.toUpperCase()) &&
    !method.isHidden &&
    !method.isFiat &&
    !!method.isTestnet === testMode

  const canContinue = isHedged
    ? !!hedgedDestinationMethod && !!selectedCustomer && !!selectedExternalAccount
    : !!selectedFundingMethod &&
      !!selectedCustomer &&
      !!selectedExternalAccount &&
      (!isFiat || (!!selectedPayoutMethod && 'ticker' in selectedPayoutMethod && selectedPayoutMethod.ticker === 'EUR'))

  return (
    <>
      <div style={{ maxWidth: 640, width: '100%', display: 'flex', flexDirection: 'column', gap: 18 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 500, color: 'var(--ink, #20242d)' }}>Choose payout type</h1>
            <SubTitle>
              Choose how the recipient should receive funds, then select the funding asset and destination.
            </SubTitle>
          </div>
          <div style={{ fontSize: 14, display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
            <SecondaryButton
              onClick={() => {
                setPayoutType(PayoutType.Crypto)
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                border: payoutType === PayoutType.Crypto ? '1px solid var(--border-strong, #d7dbe3)' : '1px solid var(--border-soft, #eceff3)',
                borderRadius: 14,
                padding: '12px 14px',
                minHeight: 78,
                justifyContent: 'flex-start',
                background: payoutType === PayoutType.Crypto ? 'var(--surface-hover, #f7f7f8)' : 'var(--surface, #ffffff)',
                color: payoutType === PayoutType.Crypto
                  ? RADOM_COLORS.BLACK
                  : RADOM_COLORS.GRAY_DARKEST
              }}
              type='button'>
              <Checkbox size={30} color='#111111'
                checked={payoutType === PayoutType.Crypto} />
              <div style={{ display: 'flex', gap: 4, flex: 1, flexDirection: 'column', alignItems: 'flex-start', minWidth: 0, textAlign: 'left' }}>
                <span style={{ fontWeight: 500 }}>{PayoutType.Crypto}</span>
                <span style={{ color: RADOM_COLORS.GRAY_DARKER, fontSize: 13, lineHeight: 1.35 }}>
                      Payout to a crypto address
                </span>
              </div>
            </SecondaryButton>

            <SecondaryButton
              onClick={() => {
                setPayoutType(PayoutType.Fiat)
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                border: payoutType === PayoutType.Fiat ? '1px solid var(--border-strong, #d7dbe3)' : '1px solid var(--border-soft, #eceff3)',
                borderRadius: 14,
                padding: '12px 14px',
                minHeight: 78,
                justifyContent: 'flex-start',
                background: payoutType === PayoutType.Fiat ? 'var(--surface-hover, #f7f7f8)' : 'var(--surface, #ffffff)',
                color: payoutType === PayoutType.Fiat
                  ? RADOM_COLORS.BLACK
                  : RADOM_COLORS.GRAY_DARKEST
              }}
              type='button'>
              <Checkbox size={30} color='#111111'
                checked={payoutType === PayoutType.Fiat} />
              <div style={{ display: 'flex', flex: 1, flexDirection: 'column', alignItems: 'flex-start', gap: 4, minWidth: 0, textAlign: 'left' }}>
                <span style={{ fontWeight: 500 }}>{PayoutType.Fiat}</span>
                <span style={{ color: RADOM_COLORS.GRAY_DARKER, fontSize: 13, lineHeight: 1.35 }}>
            Payout to a bank account
                </span>
              </div>
            </SecondaryButton>
          </div>
        </div>
        <div style={{ display: 'flex', padding: 18, flexDirection: 'column', borderRadius: 18, border: '1px solid var(--border-soft, #eceff3)', background: 'var(--surface, #ffffff)', gap: 16 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <h2 style={{ fontSize: 18, fontWeight: 500, lineHeight: '24px', margin: 0, color: 'var(--ink, #20242d)' }}>{payoutType} payout</h2>
            <SubTitle>{payoutType === PayoutType.Crypto ? 'Send crypto directly to an address' : 'Convert crypto to fiat and send to a bank account'}</SubTitle>
          </div>
          <SectionBreakerHorizontal />
          {!isFiat && !testMode && stablecoinFundingEnabled && (
            <SecondaryButton
              onClick={() => onToggleStablecoinFunding(!fundWithStablecoin)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                border: fundWithStablecoin ? '1px solid var(--border-strong, #d7dbe3)' : '1px solid var(--border-soft, #eceff3)',
                borderRadius: 14,
                padding: '12px 14px',
                justifyContent: 'flex-start',
                background: fundWithStablecoin ? 'var(--surface-hover, #f7f7f8)' : 'var(--surface, #ffffff)',
                color: fundWithStablecoin ? RADOM_COLORS.BLACK : RADOM_COLORS.GRAY_DARKEST
              }}
              type='button'>
              <Checkbox size={24} color='#111111' checked={fundWithStablecoin} />
              <div style={{ display: 'flex', gap: 4, flex: 1, flexDirection: 'column', alignItems: 'flex-start', minWidth: 0, textAlign: 'left' }}>
                <span style={{ fontWeight: 500 }}>Fund with stablecoin balance</span>
                <span style={{ color: RADOM_COLORS.GRAY_DARKER, fontSize: 13, lineHeight: 1.45, textAlign: 'left' }}>
                  Send a different crypto at a guaranteed rate
                </span>
              </div>
            </SecondaryButton>
          )}
          <div style={{ fontSize: 14, display: 'flex', flexDirection: 'column', gap: 14 }}>

            {isHedged
              ? <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <InputLabel>Funding currency</InputLabel>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
                  {(['USDC', 'USDT'] as PayoutFundingCurrency[]).map(currency => (
                    <SecondaryButton
                      key={currency}
                      onClick={() => setFundingCurrency(currency)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        border: fundingCurrency === currency ? '1px solid var(--border-strong, #d7dbe3)' : '1px solid var(--border-soft, #eceff3)',
                        borderRadius: 10,
                        padding: '10px 12px',
                        justifyContent: 'flex-start',
                        background: fundingCurrency === currency ? 'var(--surface-hover, #f7f7f8)' : 'var(--surface, #ffffff)',
                        color: fundingCurrency === currency ? RADOM_COLORS.BLACK : RADOM_COLORS.GRAY_DARKEST
                      }}
                      type='button'>
                      <Checkbox size={20} color='#111111' checked={fundingCurrency === currency} />
                      <HedgedFundingOption currency={currency} />
                    </SecondaryButton>
                  ))}
                </div>
              </div>
              : isFiat
                ? <ManagedPaymentMethodSelector
                  height={32}
                  showLabel
                  hideDisabled
                  placeholderText='Select asset'
                  selectedMethods={selectedFundingMethod ? [selectedFundingMethod] : []}
                  setSelectedMethods={(methods) => {
                    setSelectedFundingMethod(methods[methods.length - 1])
                  }}
                  testnetOnly={testMode}
                  label='Funding asset'
                  hideCheckbox
                  filterMethod={isFiatFundingMethod}
                  allowedNetworks={[fiatFundingNetwork]}
                />
                : <ManagedPaymentMethodSelector
                  height={32}
                  showLabel
                  hideDisabled
                  placeholderText='Select asset'
                  selectedMethods={selectedFundingMethod ? [selectedFundingMethod] : []}
                  setSelectedMethods={(methods) => {
                    setSelectedFundingMethod(methods[methods.length - 1])
                  }}
                  testnetOnly={testMode}
                  label='Funding asset'
                  hideCheckbox
                  allowedNetworks={['Ethereum', 'Tron', 'SepoliaTestnet']}
                />}
          </div>
          {isHedged && (
            <div style={{ fontSize: 14, display: 'flex', flexDirection: 'column', gap: 14 }}>
              <ManagedPaymentMethodSelector
                height={32}
                showLabel
                hideDisabled
                placeholderText='Select asset'
                selectedMethods={hedgedDestinationMethod ? [hedgedDestinationMethod] : []}
                setSelectedMethods={(methods) => {
                  setSelectedPayoutMethod(methods[methods.length - 1])
                  setSelectedExternalAccount(undefined)
                }}
                label='Payout asset'
                hideCheckbox
                filterMethod={m => isStablecoinDestinationMethod(m) || getDestinationStatus(m).available}
                getMethodAvailability={m => ({ available: getDestinationStatus(m).available })}
              />

            </div>
          )}
          {/* Payout Asset selector — only for fiat */}
          { isFiat && (
            <div style={{ fontSize: 14 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <InputLabel>Payout Asset</InputLabel>
                <SearchMultiselect
                  outerStyle={{ minHeight: 36, alignItems: 'center' }}
                  disabled={false}
                  placeholder="Select asset"
                  itemArray={Currencies.filter(c => c.ticker === 'EUR')}
                  selectedItems={
                    selectedPayoutMethod && 'icon' in selectedPayoutMethod ? [selectedPayoutMethod] : []
                  }
                  setSelectedItems={(c) => setSelectedPayoutMethod(c[0])}
                  keyExtractor={(c) => c.ticker}
                  labelExtractor={(c) => (
                    <div
                      style={{ display: 'flex', gap: 5, alignItems: 'center' }}
                    >
                      {c.icon}
                      <span>{c.ticker}</span>
                      <span style={{ color: 'var(--text-soft, #7d8492)' }}>· {assetName(c.ticker)}</span>
                    </div>
                  )}
                  isSingleSelect
                />
              </div>
            </div>)}
          <SectionBreakerHorizontal/>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <InputLabel>Contact</InputLabel>
            <SearchMultiselect
              isSingleSelect={true}
              outerStyle={{ minHeight: 36, alignItems: 'center' }}
              isLoading={isLoadingCustomer}
              loadInsideList={true}
              placeholder="Select contact"
              dropdownPlaceholder="No contacts found"
              itemArray={customers}
              selectedItems={selectedCustomer ? [selectedCustomer] : []}
              setSelectedItems={newItems => {
                setSelectedCustomer(newItems[0])
                setSelectedExternalAccount(undefined)
              }}
              headers={[
                <AddButton key="create_customer" onClick={() => setCustomerFormVisible(true)}>
                  <span>+ Create contact</span>
                </AddButton>
              ]}
              keyExtractor={(customer) => `${customer.id}${customer.name}${customer.email}`}
              labelExtractor={customer => customer.name ? `${customer.name} (${customer.email})` : customer.email}
              onNewItem={() => setCustomerFormVisible(true)}
            />
          </div>

          {selectedCustomer && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <InputLabel>{isFiat ? 'External account' : 'Payout address'}</InputLabel>
              {isLoadingExternalAccounts
                ? <span style={{ fontSize: 13, color: RADOM_COLORS.GRAY_DARKEST }}>Loading accounts...</span>
                : <SearchMultiselect
                  isSingleSelect={true}
                  outerStyle={{ minHeight: 36, alignItems: 'center' }}
                  placeholder={isFiat ? 'Select external account' : 'Select payout address'}
                  dropdownPlaceholder={isFiat ? 'No bank accounts found' : 'No crypto accounts found'}
                  itemArray={filteredExternalAccounts}
                  selectedItems={selectedExternalAccount ? [selectedExternalAccount] : []}
                  setSelectedItems={newItems => setSelectedExternalAccount(newItems[0])}
                  headers={[
                    <AddButton key="create_account" onClick={() => setCreateAccountModalOpen(true)}>
                      <span>+ Create account</span>
                    </AddButton>
                  ]}
                  keyExtractor={(account) => account.id}
                  labelExtractor={(account) => <ExternalAccountSelectorTag account={account} />}
                />
              }
            </div>
          )}

          <PrimaryButton onClick={onContinue} disabled={!canContinue} style={{ width: '100%' }}>
            Continue
          </PrimaryButton>

        </div>

      </div>
      <Modal
        visible={customerFormVisible}
        onClose={() => setCustomerFormVisible(false)}
        title='Create contact'>
        <div style={{ overflow: 'auto' }}>
          <CustomerForm onCreateCustomer={loadCustomers} />
        </div>
      </Modal>

      {selectedCustomer && (
        <CreateExternalAccountModal
          visible={createAccountModalOpen}
          onClose={() => setCreateAccountModalOpen(false)}
          title='Create payout account'
          customerId={selectedCustomer.id}
          contact={selectedCustomer}
          preselectedMethod={accountCreationMethod}
          forcedCategory={isFiat ? PayoutAccountCategory.Fiat : PayoutAccountCategory.Crypto}
          onAccountCreated={async () => {
            setCreateAccountModalOpen(false)
            await onExternalAccountCreated()
          }}
        />
      )}
    </>
  )
}

export default SelectAssets
